import { lazy } from 'react';

import type { Capabilities, SubscriptionDetail } from '@rtkwlf/rendall';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet } from 'react-router-dom';

import { FeatureFlagGuard } from '@platform/client/feature-flags';
import type { FeatureConfig } from '@platform/client/features';

import { RiskDashboardServerApiProvider } from '@apis/risk-dashboard-server/risk-dashboard-server-api-provider';
import { CapabilitiesGuard } from '@packages/components/capabilities/capabilities-guard';
import { LegacyCapabilitiesGuard } from '@packages/components/capabilities/legacy-capabilities-guard';
import { SubscriptionsGuard } from '@packages/components/subscriptions/subscriptions-guard/subscriptions-guard';
import { PagePlain } from '@packages/fenrir/pages/page-plain/page-plain';
import { useFeatureFlag } from '@packages/platform/feature-flags/use-feature-flag';
import { hasAnyRequiredProperty } from '@packages/utils/has-any-required-property';

import { ScannerUpgradeScheduleGuard } from './components/scanner-upgrade-schedule-guard/scanner-upgrade-schedule-guard';
import i18n from './locales/i18n';
import { AGENT_SETTINGS_READ_CAPABILITY } from './pages/agents/components/agent-settings/agent-settings-layout/agent-settings-layout';
import { AccountInformationStep } from './pages/cloud-scanners/components/steps/account-information/account-information';
import { CloudApplicationStep } from './pages/cloud-scanners/components/steps/cloud-application/cloud-application';
import NotFoundPage from './pages/cloud-scanners/pages/not-found/not-found.page';
import CredentialedScanningPage from './pages/scanners/pages/credentialed-scanning/credentialed-scanning.page';
import HostDnsServerPage from './pages/scanners/pages/host-dns-servers/host-dns-servers.page';
import ScanExclusionPage from './pages/scanners/pages/scan-exclusion/scan-exclusion.page';
import ScannerInformationPage from './pages/scanners/pages/scanner-information/scanner-information.page';
import ScanningQueuePage from './pages/scanners/pages/scanning-queue/scanning-queue.page';
import ScanningSchedulePage from './pages/scanners/pages/scanning-schedule/scanning-schedule.page';

const overviewCapabilities = {
  canViewAgent: true,
  canViewCloudSensor: true,
  canViewScanner: true,
  canViewSensor: true,
};
const sensorsCapabilities = { canViewSensor: true };
const agentsCapabilities = { canViewAgent: true };
const agentsSettingsCapabilities = { canViewAgentSettings: true };
const scannerCapabilities = { canViewScanner: true };
const scannerScheduleCapabilities = { canViewScanSchedules: true };
const cloudCapabilities = { canViewCloudSensor: true };
const logSourcesSubscriptions: Partial<SubscriptionDetail> = {
  managedRisk: true,
  managedDetectionResponse: true,
};

const ScannerPageV1 = lazy(
  () => import('./pages/scanners/pages/scanners/scanners.page'),
);

const ScannerPageV2 = lazy(
  () => import('./pages/scanners/pages/scanners/scanners.page-v2'),
);

const ScannerPage: React.FC = () => {
  const [scannerPageVersion] = useFeatureFlag('SCANNER_TABLE_DATAGRID');
  return scannerPageVersion ? <ScannerPageV2 /> : <ScannerPageV1 />;
};

const ScanSchedulesPageV1 = lazy(
  () =>
    import('./pages/scan-schedules/pages/scan-schedules/scan-schedules.page'),
);
const ScanSchedulesPageV2 = lazy(
  () =>
    import(
      './pages/scan-schedules/pages/scan-schedules/scan-schedules-v2.page'
    ),
);
const ScanSchedulesPage: React.FC = () => {
  const [v2TableFF] = useFeatureFlag('SCAN_SCHEDULES_TABLE_V2');
  const { t } = useTranslation('telemetryScanSchedules');

  return (
    <PagePlain title={t('title')}>
      {v2TableFF ? <ScanSchedulesPageV2 /> : <ScanSchedulesPageV1 />}
    </PagePlain>
  );
};

/**
 * TODO: Review pages to be able to use lazy loading and avoid page reloading.
 */
export default {
  routes: [
    {
      path: 'telemetry',
      children: [
        {
          element: (
            <LegacyCapabilitiesGuard capabilities={overviewCapabilities} />
          ),
          children: [
            {
              index: true,
              lazy: () => import('./pages/overview/overview.page'),
            },
          ],
        },
        {
          path: 'agents',
          element: (
            <LegacyCapabilitiesGuard capabilities={agentsCapabilities} />
          ),
          children: [
            {
              index: true,
              lazy: () => import('./pages/agents/pages/agents.page'),
            },
            {
              path: 'settings',
              element: (
                <LegacyCapabilitiesGuard
                  capabilities={agentsSettingsCapabilities}
                >
                  <CapabilitiesGuard
                    capabilities={[AGENT_SETTINGS_READ_CAPABILITY]}
                  />
                </LegacyCapabilitiesGuard>
              ),
              lazy: () =>
                import(
                  './pages/agents/components/agent-settings/agent-settings-layout/agent-settings-layout'
                ),
              children: [
                {
                  path: 'configuration',
                  lazy: () =>
                    import(
                      './pages/agents/pages/agent-settings/agent-configurations.page'
                    ),
                },
                {
                  path: 'internal-configuration',
                  lazy: () =>
                    import(
                      './pages/agents/pages/agent-settings/agent-internal-configurations.page'
                    ),
                },
                {
                  path: 'agent-administration',
                  lazy: () =>
                    import(
                      './pages/agents/pages/agent-settings/agent-administration.page'
                    ),
                },
                {
                  path: 'create-group',
                  lazy: () =>
                    import(
                      './pages/agents/pages/agent-settings/create-new-group.page'
                    ),
                },
                {
                  path: 'group',
                  children: [
                    {
                      index: true,
                      element: <Navigate to="default" replace />,
                    },
                    {
                      path: 'default',
                      lazy: () =>
                        import(
                          './pages/agents/pages/agent-settings/agent-configurations-v2.page'
                        ),
                    },
                    {
                      path: ':groupId',
                      lazy: () =>
                        import(
                          './pages/agents/pages/agent-settings/edit-group.page'
                        ),
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: 'sensors',
          element: (
            <LegacyCapabilitiesGuard capabilities={sensorsCapabilities} />
          ),
          children: [
            {
              index: true,
              lazy: () => import('./pages/sensors/sensors.page'),
            },
            {
              path: 'add',
              lazy: () =>
                import('./pages/sensors/add-sensors-page/add-sensors.page'),
            },
            {
              path: 'view/:sensorUuid',
              lazy: () =>
                import('./pages/sensors/view-sensor-page/view-sensor.page'),
            },
          ],
        },
        {
          path: 'scanners',
          element: (
            <LegacyCapabilitiesGuard capabilities={scannerCapabilities} />
          ),
          children: [
            {
              index: true,
              element: <ScannerPage />,
            },
            {
              path: 'add',
              element: (
                <FeatureFlagGuard featureFlag="SCANNER_CONFIGURE_ACTIVATE" />
              ),
              children: [
                {
                  index: true,
                  lazy: () =>
                    import(
                      './pages/scanners/pages/add-scanner/add-scanner.page'
                    ),
                },
              ],
            },
            {
              path: ':scannerId',
              lazy: () =>
                import(
                  './pages/scanners/pages/scanners-config/scanners-config.page'
                ),
              children: [
                {
                  index: true,
                  element: <Navigate to="scannerInformation" replace />,
                },
                {
                  path: 'scannerInformation',
                  element: <ScannerInformationPage />,
                },
                {
                  path: 'hostDnsServers',
                  element: <HostDnsServerPage />,
                },
                {
                  path: 'scanExclusion',
                  element: <ScanExclusionPage />,
                },
                {
                  path: 'scanningQueue',
                  element: <ScanningQueuePage />,
                },
                {
                  path: 'scanningSchedule',
                  element: <ScanningSchedulePage />,
                },
                {
                  path: 'credentialedScanning',
                  element: <CredentialedScanningPage />,
                },
                {
                  element: (
                    <FeatureFlagGuard featureFlag="SCANNER_CONFIGURE_ACTIVATE" />
                  ),
                  children: [
                    {
                      path: 'networkConfiguration',
                      lazy: () =>
                        import(
                          './pages/scanners/pages/network-configuration/network-configuration.page'
                        ),
                    },
                    {
                      path: 'shippingDetails',
                      lazy: () =>
                        import(
                          './pages/scanners/pages/shipping-details/shipping-details.page'
                        ),
                    },
                  ],
                },
                {
                  path: 'scannerUpgradeSchedule',
                  element: <ScannerUpgradeScheduleGuard />,
                  children: [
                    {
                      index: true,
                      lazy: () =>
                        import(
                          './pages/scanners/pages/scanner-upgrade-schedule-tab/scanner-upgrade-schedule-tab'
                        ),
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: 'scan-schedules',
          element: (
            <LegacyCapabilitiesGuard capabilities={scannerScheduleCapabilities}>
              <RiskDashboardServerApiProvider>
                <Outlet />
              </RiskDashboardServerApiProvider>
            </LegacyCapabilitiesGuard>
          ),
          children: [
            {
              index: true,
              element: <ScanSchedulesPage />,
            },
            {
              path: 'create',
              lazy: () =>
                import(
                  './pages/scan-schedules/pages/create-new-scan-schedule/create-new-scan-schedule.page'
                ),
            },
            {
              path: 'edit/:scheduleId',
              lazy: () =>
                import(
                  './pages/scan-schedules/pages/edit-scan-schedule/edit-scan-schedule.page'
                ),
            },
          ],
        },
        {
          path: 'log-sources',
          element: (
            <SubscriptionsGuard subscriptions={logSourcesSubscriptions} />
          ),
          children: [
            {
              index: true,
              lazy: () => import('./pages/log-sources/log-sources.page'),
            },
          ],
        },
        {
          path: 'coverage-score',
          element: (
            <SubscriptionsGuard
              subscriptions={{
                managedDetectionResponse: true,
                dataExploration: true,
                dataExplorationLite: true,
              }}
            />
          ),
          children: [
            {
              index: true,
              lazy: () => import('./pages/coverage-score/CoverageScore.page'),
            },
          ],
        },
        {
          path: 'cloud',
          element: <LegacyCapabilitiesGuard capabilities={cloudCapabilities} />,
          children: [
            {
              index: true,
              lazy: () =>
                import(
                  './pages/cloud-sensors/pages/cloud-sensors/cloud-sensors.page'
                ),
            },
            {
              path: 'add/:sensorType?',
              lazy: () =>
                import(
                  './pages/cloud-sensors/pages/add-cloud-sensors/add-cloud-sensors.page'
                ),
            },
            {
              path: 'view/:deploymentId',
              lazy: () =>
                import(
                  './pages/cloud-sensors/pages/view-cloud-sensors/view-cloud-sensors.page'
                ),
            },
          ],
        },
        {
          path: 'cloud-scanners',
          element: <FeatureFlagGuard featureFlag="CLOUD_SCANNERS_CSPM" />,
          children: [
            {
              index: true,
              lazy: () =>
                import(
                  './pages/cloud-scanners/pages/cloud-scanners/cloud-scanners.page'
                ),
            },
            {
              path: 'add/',
              lazy: () =>
                import(
                  './pages/cloud-scanners/pages/add-cloud-scanners/add-cloud-scanners.page'
                ),
              children: [
                {
                  path: 'cloud-application',
                  element: <CloudApplicationStep />,
                },
                {
                  path: 'account-information',
                  element: <AccountInformationStep />,
                },
              ],
            },
            {
              path: 'account/:id',
              lazy: () =>
                import(
                  './pages/cloud-scanners/pages/edit-cloud-scanners/edit-cloud-scanners.page'
                ),
            },
            {
              path: '*',
              element: <NotFoundPage />,
            },
          ],
        },
      ],
    },
  ],
  navigation: ({ legacyCapabilities, subscriptions, featureFlags }) => {
    const hasCapabilities = (capabilities: Partial<Capabilities>) =>
      legacyCapabilities &&
      hasAnyRequiredProperty(capabilities, legacyCapabilities);

    return {
      dataCollection: {
        dataCollectionOverview: hasCapabilities(overviewCapabilities)
          ? {
              title: i18n.t('telemetry:newTitle'),
              segment: '/telemetry',
              matchSegmentOnly: true,
            }
          : null,
        sensors: hasCapabilities(sensorsCapabilities)
          ? {
              title: i18n.t('telemetrySensors:title'),
              segment: '/telemetry/sensors',
            }
          : null,
        scanners: hasCapabilities(scannerCapabilities)
          ? {
              title: i18n.t('telemetryScanners:title'),
              segment: '/telemetry/scanners',
            }
          : null,
        agents: hasCapabilities(agentsCapabilities)
          ? {
              title: i18n.t('telemetryAgents:title'),
              segment: '/telemetry/agents',
            }
          : null,
        cloudSensors: hasCapabilities(cloudCapabilities)
          ? {
              title: i18n.t('telemetryCloudSensors:navItemName'),
              segment: '/telemetry/cloud',
            }
          : null,
        cloudScanners: featureFlags?.['CLOUD_SCANNERS_CSPM']
          ? {
              title: i18n.t('telemetryCloudScanners:title'),
              segment: '/telemetry/cloud-scanners',
            }
          : null,
        logSources: hasAnyRequiredProperty(
          logSourcesSubscriptions,
          subscriptions,
          false,
        )
          ? {
              title: i18n.t('logSources:title'),
              segment: '/telemetry/log-sources',
            }
          : null,
        scanSchedules: hasCapabilities(scannerScheduleCapabilities)
          ? {
              title: i18n.t('telemetryScanSchedules:title'),
              segment: '/telemetry/scan-schedules',
            }
          : null,
      },
    };
  },
} satisfies FeatureConfig;
